import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { getCookie } from '../utils/utils';
import { CloseIcon } from '../common/CloseIcon';
import { ListingCardListing } from './types';
import OffMarketBuyAndSellDetailsModal, {
  PropertyDetails,
} from '../BuildingModal/OffMarketBuyAndSellDetailsModal';
import EditListingModal from '../EditListingModal/EditListingModal';
import RemoveSellReasonModal from '../common/RemoveSellReasonModal';
import {
  shareOffMarketWillingness,
  formatAddressString,
} from '../../utils/shareUtils';
import { listingToCouldBuyForm } from '../AppContent/NewLandingPage/CouldBuyFlow/listingToCouldBuyForm';
import { startListingPriceEstimation } from '../utils/startListingPriceEstimation';

export interface UseListingManagement {
  openEdit: () => void;
  openDelete: () => void;
  openShare: () => void;
  modals: React.ReactNode;
}

// Mirrors the legacy card's edit-prefill: maps the listing's stored property_*
// fields straight onto the PropertyDetails shape the edit modal expects.
function listingToDetails(listing: ListingCardListing): PropertyDetails {
  return {
    description: listing.property_description,
    size: listing.property_size,
    rooms:
      listing.property_rooms !== undefined
        ? String(listing.property_rooms)
        : undefined,
    floor: listing.property_floor,
    hasElevator: listing.property_has_elevator,
    hasBalcony: listing.property_has_balcony,
    renovationStatus: listing.property_renovation_status,
    images: listing.images?.map((img) => ({
      id: img.id || img.url,
      url: img.url,
      thumbnailUrl: img.thumbnailUrl || img.url,
      originalUrl: img.originalUrl,
      originalThumbnailUrl: img.originalThumbnailUrl,
      enhancedUrl: img.enhancedUrl,
      enhancedThumbnailUrl: img.enhancedThumbnailUrl,
      isDisplayOptimized: img.isDisplayOptimized,
    })),
    askingPrice: listing.property_asking_price,
  };
}

export function useListingManagement(
  listing: ListingCardListing,
  opts: { onChanged: () => void },
): UseListingManagement {
  const { t, i18n } = useTranslation();
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [removeSellOpen, setRemoveSellOpen] = useState(false);
  const [isRemovingSell, setIsRemovingSell] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isSell = !!listing.could_sell;

  const openDelete = useCallback(() => {
    if (isSell) {
      setRemoveSellOpen(true);
    } else {
      setDeleteConfirmOpen(true);
    }
  }, [isSell]);

  // Route Edit by listing type: SELL keeps the legacy PUT-update modal below;
  // BUY opens the new CouldBuyFlow wizard instead (JAA-304) via the same window
  // event MyListings dispatches, since this hook has no prop path down to the
  // NewLandingPage-owned flow instance.
  const openEdit = useCallback(() => {
    if (isSell) {
      setEditOpen(true);
      return;
    }
    const { form, seed } = listingToCouldBuyForm(listing);
    window.dispatchEvent(
      new CustomEvent('jaakko:edit-buy-listing', {
        detail: { seed, prefillForm: form, willingnessId: listing._id },
      }),
    );
  }, [isSell, listing]);

  /**
   * Confirm handler of the legacy buy edit modal. Unreachable while buy edits
   * go through CouldBuyFlow (openEdit only sets editOpen for sell listings); the
   * estimate branch is kept in the same shape as the other callers and never
   * fires for buy, since the server mints no capability without could_sell.
   */
  const handleEditConfirm = useCallback(
    async (propertyDetails?: PropertyDetails) => {
      try {
        setEditLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/off-market-willingness/${listing._id}`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
            },
            body: JSON.stringify({
              property_description: propertyDetails?.description,
              property_size: propertyDetails?.size ?? null,
              property_rooms:
                propertyDetails?.rooms === '' ? null : propertyDetails?.rooms,
              property_floor: propertyDetails?.floor ?? null,
              property_has_elevator: propertyDetails?.hasElevator,
              property_has_balcony: propertyDetails?.hasBalcony,
              property_renovation_status:
                propertyDetails?.renovationStatus === ''
                  ? null
                  : propertyDetails?.renovationStatus,
              property_asking_price: propertyDetails?.askingPrice ?? null,
              property_images:
                propertyDetails?.images?.map((img) => ({
                  id: img.id,
                  url: img.url,
                  thumbnailUrl: img.thumbnailUrl,
                  originalUrl: img.originalUrl ?? null,
                  originalThumbnailUrl: img.originalThumbnailUrl ?? null,
                  enhancedUrl: img.enhancedUrl ?? null,
                  enhancedThumbnailUrl: img.enhancedThumbnailUrl ?? null,
                  isDisplayOptimized: img.isDisplayOptimized === true,
                })) || [],
            }),
          },
        );
        if (!response.ok) {
          try {
            const err = await response.json();
            if (err && err.error_code === 'SIZE_MUST_BE_GREATER_THAN_ZERO') {
              alert(t('building.offMarket.modal.sizeMustBeGreaterThanZero'));
              return;
            }
          } catch (_) {
            // ignore json parse errors, fall back to generic
          }
          throw new Error('Failed to update willingness');
        }
        // An edit that changed what the place is invalidates its Jaakko
        // estimate, so the backend mints a one-time capability and the new
        // estimate starts here — the same handoff the publish flow uses. Edits
        // that only touched photos or the asking price mint nothing.
        const updated = await response.json().catch(() => ({}));
        if (updated.price_estimation_capability) {
          void startListingPriceEstimation({
            willingnessId: listing._id,
            buildingId: listing.building_id || '',
            automationCapability: updated.price_estimation_capability,
            address: listing.address,
            propertyDetails: {
              description: propertyDetails?.description ?? '',
              size: propertyDetails?.size ?? null,
              rooms: propertyDetails?.rooms || '',
              floor: propertyDetails?.floor ?? null,
              hasElevator: propertyDetails?.hasElevator ?? false,
              hasBalcony: propertyDetails?.hasBalcony ?? false,
              renovationStatus: propertyDetails?.renovationStatus || '',
              buildingType: listing.property_building_type || '',
            },
            language: i18n.language,
          }).then((result) => {
            if (result !== 'started') return;
            window.dispatchEvent(new Event('jaakko:listings-changed'));
          });
        }
        setEditOpen(false);
        opts.onChanged();
      } catch (error) {
        console.error('Error updating willingness:', error);
        alert(t('common.error', 'An error occurred. Please try again.'));
      } finally {
        setEditLoading(false);
      }
    },
    [listing, opts, t, i18n],
  );

  // Native share sheet, falling back to the clipboard. shareOffMarketWillingness
  // already swallows its own failures, so there is nothing to unwind here.
  const handleShare = useCallback(async () => {
    await shareOffMarketWillingness({
      buildingId: listing.building_id || '',
      eventId: listing._id,
      isSellInterest: isSell,
      address: listing.address,
      t,
    });
  }, [listing.building_id, listing._id, listing.address, isSell, t]);

  const handleRemoveSell = useCallback(
    async (reason: string, reasonText?: string) => {
      try {
        setIsRemovingSell(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/off-market-willingness/${listing._id}`,
          {
            method: 'DELETE',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
            },
            body: JSON.stringify({
              removal_reason: reason,
              ...(reasonText ? { removal_reason_text: reasonText } : {}),
            }),
          },
        );
        if (!response.ok) {
          throw new Error('Failed to delete');
        }
        setRemoveSellOpen(false);
        opts.onChanged();
      } catch (err) {
        console.error('Error deleting willingness:', err);
        alert(t('common.error', 'An error occurred. Please try again.'));
      } finally {
        setIsRemovingSell(false);
      }
    },
    [listing._id, opts, t],
  );

  const handleDeleteBuy = useCallback(async () => {
    try {
      setIsDeleting(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/off-market-willingness/${listing._id}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
          },
        },
      );
      if (!response.ok) {
        throw new Error('Failed to delete');
      }
      setDeleteConfirmOpen(false);
      opts.onChanged();
    } catch (err) {
      console.error('Error deleting willingness:', err);
      alert(t('common.error', 'An error occurred. Please try again.'));
    } finally {
      setIsDeleting(false);
    }
  }, [listing._id, opts, t]);

  const modals = (
    <>
      {editOpen &&
        (isSell ? (
          <EditListingModal
            isOpen={editOpen}
            willingnessId={listing._id}
            onClose={() => setEditOpen(false)}
            onSaved={opts.onChanged}
          />
        ) : (
          <OffMarketBuyAndSellDetailsModal
            isOpen={editOpen}
            onClose={() => setEditOpen(false)}
            onConfirm={handleEditConfirm}
            actionType="buy"
            isLoading={editLoading}
            isEditMode={true}
            initialData={listingToDetails(listing)}
            willingnessId={listing._id}
            isLoggedIn
            elevated
            building={{
              id: listing.building_id,
              addresses: [listing.address],
            }}
          />
        ))}

      <RemoveSellReasonModal
        isOpen={removeSellOpen}
        isRemoving={isRemovingSell}
        onClose={() => setRemoveSellOpen(false)}
        onConfirm={handleRemoveSell}
      />

      {deleteConfirmOpen &&
        ReactDOM.createPortal(
          <div
            className="end-chat-confirmation-overlay end-chat-confirmation-overlay--elevated"
            onClick={() => !isDeleting && setDeleteConfirmOpen(false)}
          >
            <div
              className="end-chat-confirmation"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="end-chat-confirmation__header">
                <h3>
                  <Trans i18nKey="building.offMarket.modal.deleteConfirmTitle">
                    Remove listing
                  </Trans>
                </h3>
                <button
                  className="end-chat-confirmation__close"
                  onClick={() => setDeleteConfirmOpen(false)}
                  disabled={isDeleting}
                >
                  <CloseIcon color="#202125" size={16} />
                </button>
              </div>
              <div className="end-chat-confirmation__content">
                <p>
                  <Trans i18nKey="building.offMarket.modal.deleteConfirmText">
                    Are you sure you want to remove this event?
                  </Trans>
                </p>
              </div>
              <div className="end-chat-confirmation__actions">
                <button
                  className="end-chat-confirmation__button end-chat-confirmation__button--cancel"
                  onClick={() => setDeleteConfirmOpen(false)}
                  disabled={isDeleting}
                >
                  <Trans i18nKey="building.offMarket.modal.cancel">
                    Cancel
                  </Trans>
                </button>
                <button
                  className="end-chat-confirmation__button end-chat-confirmation__button--confirm"
                  onClick={handleDeleteBuy}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    t('building.offMarket.modal.loading', 'Loading...')
                  ) : (
                    <Trans i18nKey="building.offMarket.modal.delete">
                      Remove
                    </Trans>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

    </>
  );

  return {
    openEdit,
    openDelete,
    // Share straight away (native sheet, clipboard fallback) rather than via
    // SharePromptModal. That modal is the post-creation prompt — it congratulates
    // the user on an interest that "has been registered" and pitches the paid
    // boost, which reads wrong on a listing they made days ago and puts an ad
    // between them and the link.
    openShare: handleShare,
    modals,
  };
}
